import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import Box from "@mui/material/Box";
import { Colors } from "../../../configs";
import { CustomButton } from "../../atoms";
import { primaryAlpha } from "../../../configs/colors";

interface DeleteConfirmationModalProps {
    open: boolean;
    title?: string;
    message?: React.ReactNode;
    positiveButtonText?: string;
    negativeButtonText?: string;
    onCancel: () => void;
    onConfirm: () => void;
}

export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
    open,
    title = "Delete Confirmation",
    message = "Are you sure to delete this note?",
    positiveButtonText = "Delete",
    negativeButtonText = "Cancel",
    onCancel,
    onConfirm,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onCancel}
            PaperProps={{
                sx: {
                    padding: "2.5rem 2.8rem",
                    borderRadius: "0.5rem",
                    minWidth: "35rem",
                    position: "relative",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                },
            }}
        >
            <IconButton
                onClick={onCancel}
                sx={{
                    position: "absolute",
                    top: "1.2rem",
                    right: "1.2rem",
                    color: Colors.MAIN_GRAY,
                    padding: "0.2rem",
                }}
            >
                <CancelOutlinedIcon sx={{ fontSize: "2rem" }} />
            </IconButton>

            <DialogTitle
                sx={{
                    fontSize: "2.125rem", // Standard Material-UI h4 variant size
                    fontWeight: "bold",
                    color: Colors.TEXT_GRAY_DARK,
                    padding: 0,
                    paddingBottom: "1.5rem",
                    paddingRight: "2rem",
                }}
            >
                {title}
            </DialogTitle>

            <DialogContent sx={{ padding: 0 }}>
                <DialogContentText
                    sx={{
                        fontSize: "1.5rem",
                        color: Colors.TEXT_GRAY_DARK,
                        fontWeight: 500,
                        marginBottom: "3rem",
                    }}
                >
                    {message}
                </DialogContentText>
            </DialogContent>

            <DialogActions
                sx={{
                    padding: 0,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "1.5rem",
                }}
            >
                <CustomButton
                    onClick={onCancel}
                    sx={{
                        flex: 1,
                        backgroundColor: "transparent",
                        color: Colors.PRIMARY,
                        border: `1px solid ${Colors.PRIMARY}`,
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        padding: "0.75rem 0",
                        borderRadius: "0.3rem",
                        "&:hover": {
                            backgroundColor: primaryAlpha(0.04),
                        },
                    }}
                >
                    {negativeButtonText}
                </CustomButton>
                <CustomButton
                    onClick={onConfirm}
                    sx={{
                        flex: 1,
                        backgroundColor: Colors.PRIMARY,
                        color: Colors.WHITE,
                        fontSize: "1.5rem",
                        fontWeight: 600,
                        padding: "0.75rem 0",
                        borderRadius: "0.3rem",
                        "&:hover": {
                            backgroundColor: Colors.PRIMARY_DARK,
                        },
                    }}
                >
                    {positiveButtonText}
                </CustomButton>
            </DialogActions>
        </Dialog>
    );
};
